import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Describes what runs today (see docs/HOW_IT_WORKS.md); no claims beyond that.
const STEPS = [
  ["how-observe", "01 · Observe", "Watch the MCP supply chain",
    "We read the official MCP registry, the npm and PyPI packages MCP servers ship as, and the tool lists that " +
    "remote MCP servers publish. Text only: we never install or run third-party code."],
  ["how-analyze", "02 · Analyze", "Compare every version",
    "Each new version is diffed against the last one we saw: new install scripts, new outbound domains, new " +
    "credential reads and rewritten tool descriptions. Deterministic rules score the change; a model looks only " +
    "at flagged and sampled cases."],
  ["how-disclose", "03 · Disclose", "Confirmed before published",
    "A person reviews what the rules flag. Only confirmed findings are published, with evidence reduced to a " +
    "summary and a hash, and every lookup response is signed."],
];

export default function Home() {
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    fetch(`${API}/public/reports`).then(r => r.json())
      .then(d => setLatest((d.reports || [])[0] || null))
      .catch(() => setLatest(null));
  }, []);
  return (
    <section data-testid="home-section" className="space-y-16">
      <div className="hs-hero" data-testid="home-hero">
        {/* The herd pattern (brand/web), behind the content; it fades out on the left where the text sits. */}
        <img src="/brand/web/hero-herd-dark.svg" alt="" aria-hidden="true" className="hs-hero-art hs-dark-only" />
        <img src="/brand/web/hero-herd-light.svg" alt="" aria-hidden="true" className="hs-hero-art hs-light-only" />
        <div className="hs-hero-content space-y-6">
          <p data-testid="home-eyebrow" className="hs-eyebrow">Observatory · MCP supply chain · Change tracking</p>
          <h1 data-testid="home-title" className="hs-display">Herd immunity for AI agents.</h1>
          <p data-testid="home-sub" className="text-lg text-muted hs-prose">
            We continuously scan the public MCP ecosystem (the official registry, npm and PyPI packages, and remote MCP
            servers), track every version, and flag risky changes: hidden instructions to AI agents, new install scripts,
            unexpected data destinations, and servers that ask for credentials or payment authorisations.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/check" data-testid="home-cta-check" className="hs-btn hs-btn-primary">Check an item</Link>
            <Link to="/early-access" data-testid="home-cta-early-access" className="hs-btn hs-btn-secondary">Get early access</Link>
            <Link to="/servers" data-testid="home-cta-servers" className="hs-btn hs-btn-secondary">Browse server profiles</Link>
            <Link to="/reports" data-testid="home-cta-reports" className="hs-btn hs-btn-secondary">Latest reports</Link>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {STEPS.map(([testid, eyebrow, title, body]) => (
          <div key={testid} data-testid={testid} className="hs-card">
            <div className="hs-eyebrow mb-2">{eyebrow}</div>
            <h2 className="hs-display text-xl mb-2">{title}</h2>
            <p className="text-muted m-0">{body}</p>
          </div>
        ))}
      </div>

      {latest && (
        <div data-testid="home-latest-report" className="hs-card">
          <div className="hs-eyebrow mb-1">Latest report · {latest.period}</div>
          <p className="text-fg mb-2">{latest.summary || ""}</p>
          <Link to={`/reports/${latest.period}`} className="hs-link">Read {latest.period} →</Link>
        </div>
      )}
    </section>
  );
}
